import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(
  (
    {
      className,
      align = "center",
      sideOffset = 4,
      onWheel,
      onTouchMove,
      ...props
    },
    ref
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        // Keep wheel/touch scrolling alive inside popover content that is
        // nested in a modal Dialog. Radix's Dialog wraps its content in
        // react-remove-scroll and exempts only its own subtree
        // (`shards: [contentRef]`); this content is portaled to document.body,
        // so it falls outside that shard. react-remove-scroll's handler —
        // registered as `document.addEventListener("wheel", …, {passive:false})`,
        // i.e. BUBBLE phase, not capture — then calls preventDefault() on every
        // wheel event over us. The list keeps its CSS scrollbar (and still
        // responds to dragging and arrow keys), but the wheel does nothing.
        // Because that listener sits on `document`, stopping propagation here
        // means it never runs. Consumers' own handlers still fire first;
        // `DateTimePicker.Field` hand-rolled this same workaround on one inner
        // div before it was fixed here for every consumer.
        onWheel={(event) => {
          onWheel?.(event);
          event.stopPropagation();
        }}
        onTouchMove={(event) => {
          onTouchMove?.(event);
          event.stopPropagation();
        }}
        className={cn(
          // pointer-events-auto is defensive against a modal Dialog setting
          // `document.body.style.pointerEvents = "none"` while this Popover's
          // content is open above/inside it — without it, this content would
          // silently inherit `none` and become unclickable. Forcing it back
          // to `auto` here is always correct: open popover content should
          // never be non-interactive. (Radix's Select content does this
          // internally already; Popover.Content does not.)
          //
          // Note: this repo's package.json `overrides` block pins
          // @radix-ui/react-dismissable-layer (and its focus-scope/portal
          // dependents) to one exact version, so Dialog and Popover always
          // share a single dismissable-layer module instance/layer stack
          // today — an earlier version of this comment claimed they could
          // resolve to separate instances, which isn't true under that
          // override. This class stays as a cheap, harmless safeguard
          // regardless of that pin.
          "pointer-events-auto z-50 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  )
);
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
